import { getApiBaseUrl } from '../api/http_client.js';

const { SignInWithApple } = ( window as any ).Capacitor?.Plugins ?? {};
const { Browser }         = ( window as any ).Capacitor?.Plugins ?? {};
const { App }             = ( window as any ).Capacitor?.Plugins ?? {};

const APPLE_SERVICES_ID = 'com.matchstars.smeshroyale.web';
const CACHED_KEY        = 'native_apple_id';
const POLL_INTERVAL_MS  = 1000;
const TIMEOUT_MS        = 5 * 60 * 1000;

function getBaseUrl(): string
{
  return getApiBaseUrl().replace( /\/+$/, '' );
}

async function pollForToken( state: string ): Promise<{ identityToken: string; userId: string } | null>
{
  try
  {
    const res = await fetch( `${ getBaseUrl() }/api/auth/apple/status?state=${ state }` );
    if ( !res.ok ) return null;
    const data = await res.json() as { pending?: boolean; identityToken?: string; userId?: string };
    if ( data.pending || !data.identityToken ) return null;
    return { identityToken: data.identityToken, userId: data.userId ?? '' };
  }
  catch
  {
    return null;
  }
}

function signInAndroid(): Promise<{ identityToken: string; userId: string }>
{
  return new Promise( ( resolve, reject ) =>
  {
    const state = crypto.randomUUID();

    const params = new URLSearchParams( {
      client_id:     APPLE_SERVICES_ID,
      redirect_uri:  `${ getBaseUrl() }/api/auth/apple/callback`,
      response_type: 'code id_token',
      response_mode: 'form_post',
      scope:         'openid email name',
      nonce:         crypto.randomUUID(),
      state,
    } );

    let done = false;

    const clearAll = () =>
    {
      clearInterval( pollTimer );
      clearTimeout( timeoutTimer );
      appStateListener?.remove();
    };

    // When user closes Custom Tab — app comes back to foreground
    const appStateListener = App.addListener( 'appStateChange', ( { isActive }: { isActive: boolean } ) =>
    {
      if ( !isActive || done ) return;
      // Give polling one more second before rejecting
      setTimeout( async () =>
      {
        if ( done ) return;
        const result = await pollForToken( state );
        if ( result )
        {
          done = true;
          clearAll();
          resolve( result );
        }
        else
        {
          done = true;
          clearAll();
          reject( new Error( 'Closed by user' ) );
        }
      }, 1000 );
    } );

    const pollTimer = setInterval( async () =>
    {
      if ( done ) return;
      const result = await pollForToken( state );
      if ( !result ) return;
      done = true;
      clearAll();
      Browser.close().catch( () => {} );
      console.log( '[AppleSignIn] token received via polling' );
      resolve( result );
    }, POLL_INTERVAL_MS );

    const timeoutTimer = setTimeout( () =>
    {
      if ( done ) return;
      done = true;
      clearAll();
      Browser.close().catch( () => {} );
      reject( new Error( 'Apple Sign-In timeout' ) );
    }, TIMEOUT_MS );

    Browser.open( {
      url:               `https://appleid.apple.com/auth/authorize?${ params }`,
      presentationStyle: 'popover',
    } ).catch( ( e: unknown ) =>
    {
      clearAll();
      reject( e );
    } );
  } );
}

export const AppleSignInPlugin = {
  isAvailable(): boolean
  {
    const platform = ( window as any ).Capacitor?.getPlatform?.();
    if ( platform === 'ios' ) return !!SignInWithApple;
    if ( platform === 'android' ) return !!Browser && !!App;
    return false;
  },

  async getCachedId(): Promise<string | null>
  {
    return localStorage.getItem( CACHED_KEY );
  },

  setCachedId( id: string ): void
  {
    localStorage.setItem( CACHED_KEY, id );
  },

  async signIn(): Promise<{ identityToken: string; userId: string }>
  {
    const platform = ( window as any ).Capacitor?.getPlatform?.();

    if ( platform === 'android' ) return signInAndroid();

    // iOS — native plugin
    const result = await SignInWithApple.authorize( {
      clientId:    'com.matchstars.royalsmash.web',
      redirectURI: `${ getBaseUrl() }/api/auth/apple/callback`,
      scopes:      'email name',
      nonce:       crypto.randomUUID(),
      state:       crypto.randomUUID(),
    } );

    const { identityToken, user } = result.response;
    if ( !identityToken ) throw new Error( 'No identityToken in Apple response' );

    return { identityToken, userId: user ?? '' };
  },

  async signOut(): Promise<void>
  {
    localStorage.removeItem( CACHED_KEY );
  },
};
